<script lang="ts">
    import { onMount } from 'svelte';
    import { page } from '$app/stores';
    import { TRANSITION_DELAY } from '$lib/global/constants';
    import { campaignClose } from '$lib/global/tracking/v4';
    import PS_TRACKING from '$lib/global/core/ps-integration';
    import { setGlobals } from '$lib/global/globals';
    import { getUserId } from '$lib/global/storage';
    import { nanoid } from 'nanoid';
    import { debugTracking } from '$lib/debug';
    import { handleAnonId } from '$lib/global/storage';

    import Header from '$lib/components/Header.svelte';
    import Footer from '$lib/components/Footer/Footer.svelte';
    import ProgressBar from '$lib/components/Progressbar.svelte';
    import AffiliateBadge from '$lib/components/AffiliateBadge.svelte';
    import CookieOverlay from '$lib/components/ConsentOverlay/ConsentOverlay.svelte';
    import BrowserStorage from '$lib/Stores/BrowserStorage.svelte';
    import Rollbar from '$lib/components/Rollbar.svelte';

    import { init } from 'emoji-mart';

    import '../app.css';
    import loadAnalytics from '$lib/global/events/loadAnalytics';
    import { integrations } from '$lib/global/tracking/integrations';

    // from +layout.js
    export let data;

    let analyticsScaffoldingDone = false;
    let mounted = false;

    onMount(() => {
        // Init Emojimart
        init({ data: data.emojiData });

        // Init AnonId
        handleAnonId();

        // Reliable enough for now
        window.addEventListener('beforeunload', () => {
            campaignClose();
            setTimeout(() => null, 500);
        });

        // Tracking
        setGlobals({
            perspectiveSessionId: nanoid(),
            anonymousId: getUserId(),
            localId: nanoid(),
            globalId: getUserId(),
            trackingVersion: data?.context?.trackingVersion,
        });

        window.PS_TRACKING = PS_TRACKING;
        // used for storing events before cookies are accepted
        window.ps_queue = [];

        const analytics = (window.analytics = window.analytics || []);
        const analytics_queue = (window.analytics_queue = window.analytics_queue || []);

        if (!analytics.initialize) {
            debugTracking('-> Initializing Analytics');

            if (analytics.invoked) {
                window.console &&
                    console.error &&
                    console.error('Tracking snippet included twice.');
            } else {
                analytics.invoked = !0;
                analytics.methods = [
                    'trackSubmit',
                    'trackClick',
                    'trackLink',
                    'trackForm',
                    'pageview',
                    'identify',
                    'reset',
                    'group',
                    'track',
                    'ready',
                    'alias',
                    'debug',
                    'page',
                    'once',
                    'off',
                    'on',
                ];

                analytics.factory = function (method: any) {
                    return function () {
                        const args = Array.prototype.slice.call(arguments);
                        args.unshift(method);
                        analytics_queue.push(args);
                        window.ps_queue.push(JSON.parse(JSON.stringify(args)));

                        return analytics;
                    };
                };

                for (let i = 0; i < analytics.methods.length; i++) {
                    const method = analytics.methods[i];
                    analytics[method] = analytics.factory(method);
                }
            }

            analyticsScaffoldingDone = true;

            const perspectiveOnly = {
                Perspective: integrations.perspective,
            };

            // always load tracking with only perspective enabled
            loadAnalytics({
                companyId: data.companyId,
                integrations: perspectiveOnly,
                preventTracking: data.analytics.preventSegment,
                analyticsLink: data.analytics.analyticsLink,
            });
        } else {
            analyticsScaffoldingDone = true;
        }

        mounted = true;
    });

    const handleLogoClick = () => {
        window.location.replace(data.homeLink);
    };
</script>

<svelte:head>
    <meta name="theme-color" content={data.theme.backgroundColor} />
    {@html `<style>
        body,html {
            background-color: ${data.theme.backgroundColor};
        }
    </style>`}
</svelte:head>

<Rollbar />

<main style="--font-family: {data.theme.typography.split(':')[0]}">
    <Header
        src={data.header.logo}
        align={data.header.align}
        size={data.header.size}
        onClick={handleLogoClick}
        bgColor={data.header.bgColor}
        showIcon={data.header.showIcon}
    />

    {#if mounted}
        <slot />
    {/if}

    <Footer
        legalLink={data.footer.legalLink}
        legalText={data.footer.legalText}
        termsLink={data.footer.termsLink}
        termsText={data.footer.termsText}
        hasCookies={data.cookieOverlay.layout !== 'none'}
        language={data.cookieOverlay.language}
        bgColorReference={!data?.footer?.box?.backgroundColor ||
        data?.footer?.box?.backgroundColor === 'transparent'
            ? data.theme.backgroundColor
            : data?.footer?.box?.backgroundColor}
        branding={data.footer.branding}
        transitionDelay={$page?.data?.components?.length * TRANSITION_DELAY}
        transitionEffect={data.theme.transition}
        box={data.footer.box}
    />

    {#if data.showProgressBar}
        <ProgressBar
            sortedPages={data.sortedPages}
            pageId={$page.data.pageId}
            color={data.theme.buttonBackgroundColor}
            hasResultPages={data.hasResultPages}
        />
    {/if}

    {#if data.showAffiliateBadge}
        <AffiliateBadge
            link={$page.data.affiliateLink}
            campaignId={$page.data.campaignId}
            companyId={$page.data.companyId}
        />
    {/if}

    {#if analyticsScaffoldingDone}
        <CookieOverlay
            layout={data.cookieOverlay.layout}
            language={data.cookieOverlay.language}
            integrations={data.integrations}
            legalLink={data.footer.legalLink}
            termsLink={data.footer.termsLink}
            campaignId={data.campaignId}
        />
    {/if}
</main>

<BrowserStorage campaignId={data.campaignId} resultPages={data.resultPages} />

<style>
    main {
        font-family: var(--font-family), ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
            'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif,
            'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
    }
</style>
